import { Fragment, useMemo } from "react";
import Button from "app/storybookComponents/Button";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectIsCurrentUserAdmin,
} from "app/containers/Global/slice";
import { GoalItem, GoalReflection } from "./types";
import { getCapitalizeWord } from "./helpers";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRight,
  faEdit,
  faLink,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import {
  capitalizeWord,
  getPeopleOrPerson,
  getS,
  getUserName,
} from "utils/helperFunctions";
import { Separator } from "app/shadcnComponents/ui/separator";
import { REFLECTION_STATUS_MAP, VISIBILITY_ICONS } from "./constants";
import { cn } from "app/lib/utils";
import ReflectionProgressBar from "./Reflection/ReflectionProgressBar";
import { faCaretRight, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import AvatarCircle from "../AvatarCircle";
import { faUsers } from "@fortawesome/pro-light-svg-icons";
import ReflectionStatusBadge from "./Reflection/ReflectionStatusBadge";
import { useUpdateGoalMutation } from "./goalsApi";

interface GoalCardProps {
  goal: GoalItem;
  onEdit: (goal: GoalItem) => void;
  onDelete: (goalId: number) => void;
  onReflect: (goalId: number) => void;
  onEditReflection: (ref: GoalReflection) => void;
  readonly?: boolean;
  canSetGoal?: boolean;
  isCardExpanded?: boolean;
  setIsCardExpanded?: (expanded: boolean) => void;
  setLinkedGoalModalSelectedGoal?: (goal: GoalItem) => void;
}

export default function GoalCard({
  goal,
  onEdit,
  onDelete,
  onReflect,
  onEditReflection,
  readonly = false,
  canSetGoal,
  isCardExpanded,
  setIsCardExpanded,
  setLinkedGoalModalSelectedGoal,
}: Readonly<GoalCardProps>) {
  const navigate = useNavigate();
  const [updateGoal] = useUpdateGoalMutation();
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const isGoalArchived = !!goal.archived;

  const mostRecentReflection = useMemo(() => {
    if (!goal.reflections?.length) return null;
    return [...goal.reflections].sort((a, b) => {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    })[0];
  }, [goal.reflections]);

  const handleParentGoalSelect = () => {
    const parent = goal.parentGoal; // adjust if your object name differs
    if (!parent) return;

    const isOrgGoal = !!parent.is_org_goal;

    if (isOrgGoal) {
      navigate(`/OrganizationGuide?tab=Goals&goalId=${parent.goalId}`);
      return;
    }

    if (isAdmin && isOrgGoal) {
      navigate(
        `/AdminConsole/Organization%20Settings/Goals?goalId=${parent.goalId}`
      );
    }

    navigate(
      `/UserGuide/${parent.userAccountId}?tab=Goals&goalId=${parent.goalId}`
    );
  };

  const onDropdownSelect = (e: string | null) => {
    switch (e) {
      case "edit":
        return onEdit(goal);
      case "delete":
        return onDelete(goal.goalId);
      case "reflect":
        return onReflect(goal.goalId);
      case "archive":
        return updateGoal({
          goalId: goal.goalId,
          payload: {
            ...goal,
            archived: true,
          },
        });
      case "unarchive":
        return updateGoal({
          goalId: goal.goalId,
          payload: {
            ...goal,
            archived: false,
          },
        });
    }
  };

  const getVisibilityText = () => {
    let text = getCapitalizeWord(goal.visibility);
    if (goal?.customAllowedUserIdsAll?.length) {
      const viewerCount = goal?.customAllowedUserIdsAll?.length || 0;
      text = `${text} · ${viewerCount} ${getPeopleOrPerson(viewerCount)}`;
    }
    return text;
  };

  const getBlankTag = (label: string | JSX.Element) => (
    <div className="h-fit px-2 py-1 rounded-full w-fit flex align-items-center gap-sm border-[1px] border-[var(--border-subtle)] bg-[var(--slate-05)] font-medium">
      <p className="text-[length:var(--fontSize-helper)]">{label}</p>
    </div>
  );

  const getHeader = () => {
    return (
      <div
        className="w-full cursor-pointer hover:bg-charcoal-05 p-lg"
        onClick={() => setIsCardExpanded?.(!isCardExpanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsCardExpanded?.(!isCardExpanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isCardExpanded}
        aria-label={`${isCardExpanded ? "Collapse" : "Expand"} goal: ${
          goal.title
        }`}
      >
        <div className="d-flex justify-between gap-sm">
          <div className="flex gap-2">
            <FontAwesomeIcon
              icon={faCaretRight}
              className={cn(
                "transition-transform duration-300",
                isCardExpanded ? "rotate-90" : "rotate-0"
              )}
            />
            <p>{goal.title}</p>
          </div>
          {!readonly && (
            <Dropdown
              onSelect={onDropdownSelect}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Dropdown.Toggle
                variant="outline-primary"
                id="dropdown-basic"
                className="no-caret"
              >
                <FontAwesomeIcon
                  icon={faEllipsis}
                  className="text-[length:var(--fontSize-helper)]"
                />
              </Dropdown.Toggle>
              {isGoalArchived ? (
                <Dropdown.Menu className="flip">
                  <Dropdown.Item eventKey="unarchive">Unarchive</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item eventKey="delete" className="danger">
                    Delete Goal
                  </Dropdown.Item>
                </Dropdown.Menu>
              ) : (
                <Dropdown.Menu className="flip">
                  <Dropdown.Item eventKey="reflect">Reflect</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item eventKey="edit">Edit Goal</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item eventKey="archive">Archive Goal</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item eventKey="delete" className="danger">
                    Delete Goal
                  </Dropdown.Item>
                </Dropdown.Menu>
              )}
            </Dropdown>
          )}
        </div>

        <div className="d-flex justify-between gap-2 align-items-end mt-[10px]">
          <div className="d-flex gap-2xl">
            {mostRecentReflection ? (
              <>
                <ReflectionStatusBadge status={mostRecentReflection.status} />
                <ReflectionProgressBar
                  reflectionStatus={mostRecentReflection.status}
                  progressPercent={mostRecentReflection.progressPercent}
                  colorOverride={"var(--interactive)"}
                />
              </>
            ) : (
              <>
                {getBlankTag("Not Started")}
                <ReflectionProgressBar progressPercent={0} />
              </>
            )}
          </div>
          <div className="d-flex gap-2 justify-content-end flex-wrap">
            {!!goal.child_goals?.length && (
              <div className="flex">
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkedGoalModalSelectedGoal?.(goal);
                  }}
                  style={{ fontWeight: 600 }}
                >
                  <FontAwesomeIcon icon={faLink} className="me-2" />
                  {goal.child_goals.length} linked goal
                  {getS(goal.child_goals.length)}
                </Button>
              </div>
            )}
            <p className="text-[color:var(--text-label)]">
              {goal.timeLeft === "Past" ? "Past" : `${goal.timeLeft} left`}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const getDateString = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getReflectionCard = (reflection: GoalReflection) => {
    const { label, statusDotColor } = REFLECTION_STATUS_MAP[reflection.status];

    return (
      <div className="d-flex gap-3 position-relative">
        {/* Timeline connector */}
        <div className="d-flex flex-col align-items-center mt-[4px] mb-[-4px]">
          <div className="w-[8px] h-[8px] rounded-full flex-shrink-0 shadow-[0_0_0_2px_var(--surface-2)] bg-[var(--slate-30)]" />
          <div className="w-[3px] flex-grow-1 min-h-[60px]  bg-[var(--slate-10)]" />
        </div>

        {/* Content */}
        <div className="flex-grow-1 pb-4">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="d-flex align-items-center gap-2 text-sm text-[color:var(--text-secondary)]">
              <span>{getDateString(reflection.created_at)}</span>
              <div
                className="w-[5px] h-[5px] rounded-full"
                style={{ backgroundColor: statusDotColor }}
              />
              <span className="font-medium">{label}</span>
              <Button
                variant="link"
                onClick={() => onEditReflection(reflection)}
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            </div>
            <ReflectionProgressBar
              reflectionStatus={reflection.status}
              progressPercent={reflection.progressPercent}
              colorOverride="var(--interactive)"
            />
          </div>
          <p className="mt-2 text-sm text-[color:var(--text-secondary)]">
            {reflection.comments}
          </p>
        </div>
      </div>
    );
  };

  const getReflectionBodySection = () => {
    if (!canSetGoal) {
      return null;
    }
    const totalReflections = goal.reflections?.length ?? 0;
    return (
      <>
        <Separator />
        <div className="p-lg rounded bg-[var(--surface-2)] ">
          <div className="d-flex justify-content-between">
            <div className="d-flex gap-2 align-items-center">
              <span className="text-[var(--text-heading)] font-semibold">
                Reflection
              </span>
              <div className="text-[color:var(--text-label)] bg-[var(--slate-10)] text-[length:var(--fontSize-helper)] rounded-full d-flex align-items-center justify-content-center h-[20px] w-[20px]">
                {totalReflections}
              </div>
            </div>
            {readonly || isGoalArchived ? null : (
              <Button
                variant="secondary-blue"
                onClick={() => onReflect(goal.goalId)}
                className="!rounded-md !font-medium"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add reflection
              </Button>
            )}
          </div>
          <div className="d-flex flex-col-reverse mt-md">
            {totalReflections ? (
              goal.reflections?.map((reflection) => (
                <Fragment key={reflection.goalsReflectionsId}>
                  {getReflectionCard(reflection)}
                </Fragment>
              ))
            ) : (
              <p>No Reflections yet</p>
            )}
          </div>
        </div>
      </>
    );
  };

  const getLinkedGoalOwners = () => {
    const userAccountIds = goal.child_goals.reduce((acc, child) => {
      acc.add(child.userAccountId);
      return acc;
    }, new Set<number>());

    const userIdArr = Array.from(userAccountIds);
    const avatars: JSX.Element[] = [];
    userIdArr.slice(0, 3).forEach((userAccountId) => {
      avatars.push(
        <div style={{ marginLeft: "-10px" }}>
          <AvatarCircle
            userAccountId={userAccountId}
            key={userAccountId}
            size="small"
            className="!border-[2px]"
          />
        </div>
      );
    });

    // if there are more than 3 users, add a "+X" badge
    if (userIdArr.length > 3) {
      avatars.push(
        <div className="ms-2">
          <span className="text-sm font-medium text-[var(--text-label)] text-[length:var(--fontSize-micro)]">
            +{userIdArr.length - 3}
          </span>
        </div>
      );
    }

    return avatars;
  };

  const getLinkedGoalsDescription = () => {
    const parts: string[] = [];
    let totalOnTrack = 0;
    let totalNeedsAttention = 0;
    let totalAtRisk = 0;
    let totalNotStarted = 0;

    goal.child_goals.forEach((child) => {
      const reflection = child.reflections?.[0];
      if (reflection?.status === "on_track") {
        totalOnTrack++;
      } else if (reflection?.status === "unlikely_to_reach") {
        totalNeedsAttention++;
      } else if (reflection?.status === "some_challenges") {
        totalAtRisk++;
      } else {
        totalNotStarted++;
      }
    });

    if (totalOnTrack > 0) {
      parts.push(`${totalOnTrack} on track`);
    }
    if (totalNeedsAttention > 0) {
      parts.push(`${totalNeedsAttention} needs attention`);
    }
    if (totalAtRisk > 0) {
      parts.push(`${totalAtRisk} at risk`);
    }
    if (totalNotStarted > 0) {
      parts.push(`${totalNotStarted} not started`);
    }

    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts.join(" and ");

    return parts.slice(0, -1).join(", ") + " and " + parts.at(-1);
  };

  const getLinkedGoalsSection = () => {
    const childGoalLength = goal.child_goals?.length ?? 0;
    if (!childGoalLength) {
      return null;
    }

    return (
      <button
        onClick={() => {
          setLinkedGoalModalSelectedGoal?.(goal);
        }}
        className="p-lg border-[1px] border-[var(--border-subtle)] rounded-lg d-flex justify-content-between align-items-center bg-[var(--surface-2)] hover:bg-interactive-bg hover:border-interactive cursor-pointer transition-colors"
      >
        <div className="d-flex align-items-center gap-3">
          <FontAwesomeIcon
            icon={faUsers}
            className="text-[color:var(--interactive)] text-[length:var(--fontSize-body)]"
          />
          <div className="text-left">
            <p className="text-[color:var(--text-heading)] font-medium mb-0">
              {childGoalLength} linked goal{getS(childGoalLength)}
            </p>
            <p className="text-[color:var(--text-secondary)] text-[length:var(--fontSize-helper)] mb-0 font-medium">
              {getLinkedGoalsDescription()}
            </p>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center">
            {getLinkedGoalOwners()}
          </div>
          <FontAwesomeIcon icon={faArrowUpRight} className="text-interactive" />
        </div>
      </button>
    );
  };

  const getBody = () => {
    if (!isCardExpanded) {
      return null;
    }
    const labelClassName =
      "text-[color:var(--text-label)] text-[length:var(--fontSize-helper)]";
    const ownerName = goal.createdById
      ? getUserName(usersById[goal.createdById])
      : "";

    return (
      <div className="p-[20px] d-flex flex-col gap-[16px]">
        <div>
          <h3 className="mb-[6px] text-black">{goal.title}</h3>
          <p className="text-[var(--text-secondary)]">{goal.successCriteria}</p>
        </div>
        {/* Grid, super small 1 column , mobile 2 columns, tablet 3 columns, desktop 4 columns */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[16px]">
          <div>
            <p className={labelClassName}>Owner</p>
            <div className="d-flex align-items-center gap-2">
              <AvatarCircle userAccountId={goal.createdById} size="small" />
              <p className="text-[length:var(--fontSize-label)]">{ownerName}</p>
            </div>
          </div>
          <div>
            <p className={labelClassName}>Status</p>
            {mostRecentReflection?.status ? (
              <ReflectionStatusBadge status={mostRecentReflection.status} />
            ) : (
              getBlankTag("Not Started")
            )}
          </div>
          <div>
            <p className={labelClassName}>Progress</p>
            <ReflectionProgressBar
              progressPercent={mostRecentReflection?.progressPercent ?? 0}
              colorOverride={"var(--interactive)"}
            />
          </div>
          <div>
            <p className={labelClassName}>Deadline</p>
            <p className="text-[length:var(--fontSize-label)]">
              {goal.dueDateFormatted}
            </p>
          </div>
          <div>
            <p className={labelClassName}>Type</p>
            {getBlankTag(capitalizeWord(goal.type))}
          </div>
          <div>
            <p className={labelClassName}>Visibility</p>
            {getBlankTag(
              <>
                <FontAwesomeIcon
                  icon={VISIBILITY_ICONS[goal.visibility]}
                  className="me-2"
                />
                {getVisibilityText()}
              </>
            )}
          </div>
          {goal?.parentGoal ? (
            <div className="col-span-2">
              <p className={labelClassName}>Parent Goal</p>
              <Button onClick={handleParentGoalSelect} variant="link">
                <p className="text-[length:var(--fontSize-label)] font-medium">
                  {goal.parentGoal.title}
                  <FontAwesomeIcon icon={faArrowUpRight} className="ms-2" />
                </p>
              </Button>
            </div>
          ) : null}
        </div>

        {getLinkedGoalsSection()}
        {getReflectionBodySection()}
      </div>
    );
  };

  return (
    <div className="figtree-container">
      {getHeader()}
      {isCardExpanded && <Separator />}
      {getBody()}
    </div>
  );
}
